<template>
  <div id="category">
    <header class="bg-cl-transparent py5 px20">
      <div class="container">
        <breadcrumbs class="pl15" :withHomepage="true" />
        <div class="row middle-sm m0">
          <div class="col-sm-12">
            <h1 class="category-title h2 mb0 mt0">
              {{ getCurrentCategory.meta_title || getCurrentCategory.name }}
            </h1>
          </div>
          <div class="col-sm-4 align-right mt0 flex justify-content-end">
            <div class="sorting">
              <label class="mr10">{{ $t('Columns') }}:</label>
              <columns @change-column="columnChange" />
            </div>
            <div class="sorting ml10">
              <sort-by
                :has-label="true"
                @change="changeFilter"
                :value="getCurrentSearchQuery.sort"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="container">
        <div class="row m0">
          <button
            class="col-xs-5 mt25 mr15 p15 mobile-filters-button bg-cl-th-accent brdr-none cl-white h5 sans-serif fs-medium-small"
            @click="openFilters"
          >
            {{ $t('Filters') }}
          </button>
          <div class="mobile-sorting col-xs-6 mt25">
            <sort-by
              @change="changeFilter"
              :value="getCurrentSearchQuery.sort"
            />
          </div>
        </div>
      </div>
    </header>
    <div class="container pb60">
      <div class="row m0 pt15">
        <div class="col-md-3 start-xs category-filters">
          <sidebar :filters="getAvailableFilters" @changeFilter="changeFilter" v-if="!isCategoryEmpty" />

          <!-- <promoted-offers /> -->

          <Box :box="box" v-if="isCalculatorVisible" :class="isCategoryEmpty ? 'mt0' : 'mt30'"></Box>

          <cms-block-banner :identifier="'bs_LEFT'" :padding="'100%'" />
          <cms-block-banner :identifier="'bs_LEFT_2'" :padding="'100%'" />
        </div>
        <div class="col-md-3 start-xs mobile-filters" v-show="mobileFilters">
          <div class="close-container absolute w-100">
            <i class="material-icons p15 close cl-accent" @click="closeFilters">close</i>
          </div>
          <sidebar class="mobile-filters-body" :filters="getAvailableFilters" @changeFilter="changeFilter" />
          <div class="relative pb20 pt15">
            <div class="brdr-top-1 brdr-cl-primary absolute divider w-100" />
          </div>
          <button-full
            class="mb20 btn__filter"
            :categoryProps="getCurrentCategory.catalog"
            @click.native="closeFilters"
          >
            {{ $t('Filter') }}
          </button-full>
        </div>
        <div class="col-md-9 px10 border-box products-list">
          <div class="p20 brdr-1 brdr-cl-primary bg-cl-white products-list-container">
            <p class="col-xs-12 end-md m0 pb20 cl-secondary">
              {{ $t('{count} items', { count: getCategoryProductsTotal }) }}
            </p>
            <div v-if="isCategoryEmpty" class="hidden-xs">
              <cms-block-chunk :identifier="'c_404_TEXT'" />
            </div>
            <lazy-hydrate :trigger-hydration="!loading" v-if="isLazyHydrateEnabled">
              <product-listing :categoryProps="getCurrentCategory.catalog" :columns="defaultColumn" :products="getCategoryProducts" :isH4="false" />
            </lazy-hydrate>
            <product-listing v-else :columns="defaultColumn" :products="getCategoryProducts" :isH4="false" />
          </div>

          <div class="py30">
            <category-description :description="getCurrentCategory.description" v-if="getCurrentCategory.description!=''" class="px30" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LazyHydrate from 'vue-lazy-hydration'
import Sidebar from '../components/core/blocks/Category/Sidebar.vue'
import ProductListing from '../components/core/ProductListing.vue'
import Breadcrumbs from '../components/core/Breadcrumbs.vue'
import SortBy from '../components/core/SortBy.vue'
import CmsBlockBanner from 'theme/components/core/blocks/Cms/BlockBanner'
import CmsBlockChunk from 'theme/components/core/blocks/Cms/BlockChunk'
import CategoryDescription from 'theme/components/core/blocks/Category/CategoryDescription'
import Box from 'theme/components/core/blocks/Box/Box'
import PromotedOffers from 'theme/components/theme/blocks/PromotedOffers/PromotedOffers'
import { isServer } from '@vue-storefront/core/helpers'
import { Logger } from '@vue-storefront/core/lib/logger'
import { getSearchOptionsFromRouteParams } from '@vue-storefront/core/modules/catalog-next/helpers/categoryHelpers'
import config from 'config'
import Columns from '../components/core/Columns.vue'
import ButtonFull from 'theme/components/theme/ButtonFull.vue'
import { mapGetters } from 'vuex'
import onBottomScroll from '@vue-storefront/core/mixins/onBottomScroll'
import { catalogHooksExecutors } from '@vue-storefront/core/modules/catalog-next/hooks'
import { currentStoreView } from '@vue-storefront/core/lib/multistore'
import { htmlDecode } from '@vue-storefront/core/filters'

const THEME_PAGE_SIZE = 50

const composeInitialPageState = async (store, route, forceLoad = false) => {
  try {
    const filters = getSearchOptionsFromRouteParams(route.params)
    const cachedCategory = store.getters['category-next/getCategoryFrom'](route.path)
    const currentCategory = cachedCategory && !forceLoad ? cachedCategory : await store.dispatch('category-next/loadCategory', { filters })
    const pageSize = store.getters['url/isBackRoute'] ? store.getters['url/getCurrentRoute'].categoryPageSize : THEME_PAGE_SIZE
    await store.dispatch('category-next/loadCategoryProducts', { route, category: currentCategory, pageSize })
    const breadCrumbsLoader = store.dispatch('category-next/loadCategoryBreadcrumbs', { category: currentCategory, currentRouteName: currentCategory.name, omitCurrent: true })

    if (isServer) await breadCrumbsLoader
    catalogHooksExecutors.categoryPageVisited(currentCategory)
  } catch (e) {
    Logger.error('Problem with setting Category initial data!', 'category', e)()
  }
}

export default {
  components: {
    LazyHydrate,
    ButtonFull,
    ProductListing,
    Breadcrumbs,
    Sidebar,
    SortBy,
    CategoryDescription,
    PromotedOffers,
    CmsBlockChunk,
    'cms-block-banner': CmsBlockBanner,
    Box,
    Columns
  },
  mixins: [onBottomScroll],
  data () {
    return {
      mobileFilters: false,
      defaultColumn: 4,
      loadingProducts: false,
      loading: true
    }
  },
  mounted () {
    const category = this.getCurrentCategory.path.split('/')[0];
    this.$route.meta.category = category;
  },
  computed: {
    ...mapGetters({
      getCurrentSearchQuery: 'category-next/getCurrentSearchQuery',
      getCategoryProducts: 'category-next/getCategoryProducts',
      getCurrentCategory: 'category-next/getCurrentCategory',
      getCategoryProductsTotal: 'category-next/getCategoryProductsTotal',
      getAvailableFilters: 'category-next/getAvailableFilters',
      getCurrentFilters: 'category-next/getCurrentFilters'      
    }),
    isLazyHydrateEnabled () {
      return config.ssr.lazyHydrateFor.includes('category-next.products')
    },
    isCategoryEmpty () {
      return this.getCategoryProductsTotal === 0
    },
    isCalculatorVisible () {
      if (!isServer) {
        return !window.location.pathname.startsWith(config.site.office_path)      
      } else {
        return true
      }
    },
    box () {
      return {
        heading: "Kalkulator oszczędności",
        image: false,
        imageAlign: "left",
        text: "Sprawdź kwotę oszczędności",
        title: "<strong>Wydajność</strong> Black Point – dziś już legendarna.",
        titleColor: "",
        type: "calc",
        width: "50",
        background: {
          ID: 1202,
          alt: "",
          author: "4",
          status: "inherit",
          subtype: "png",
          title: "bg_kalkulator1",
          type: "image",
          uploaded_to: 38,
          url: "https://blackpointpl.wpengine.com/wp-content/uploads/2021/05/bg_kalkulator1.png",
          width: 740,
          caption: "",
          date: "2021-05-05 17:47:58",
          description: "",
          filename: "bg_kalkulator1.png",
          filesize: 118462,
          height: 644,
          icon: "https://blackpointpl.wpengine.com/wp-includes/images/media/default.png",
          id: 1202,
          link: "https://blackpointpl.wpengine.com/strona-glowna/bg_kalkulator1/",
          menu_order: 0,
          mime_type: "image/png",
          modified: "2021-05-05 17:48:29",
          name: "bg_kalkulator1",
          sizes: {
            "1536x1536": "https://blackpointpl.wpengine.com/wp-content/uploads/2021/05/bg_kalkulator1.png",
            "1536x1536-height": 644,
            "1536x1536-width": 740,
            "2048x2048": "https://blackpointpl.wpengine.com/wp-content/uploads/2021/05/bg_kalkulator1.png",
            "2048x2048-height": 644,
            "2048x2048-width": 740,
            "large": "https://blackpointpl.wpengine.com/wp-content/uploads/2021/05/bg_kalkulator1.png",
            "large-height": 644,
            "large-width": 740,
            "main-slider": "https://blackpointpl.wpengine.com/wp-content/uploads/2021/05/bg_kalkulator1-646x644.png",
            "main-slider-height": 644,
            "main-slider-width": 646,
            "medium": "https://blackpointpl.wpengine.com/wp-content/uploads/2021/05/bg_kalkulator1-300x261.png",
            "medium-height": 261,
            "medium-width": 300,
            "medium_large": "https://blackpointpl.wpengine.com/wp-content/uploads/2021/05/bg_kalkulator1.png",
            "medium_large-height": 644,
            "medium_large-width": 740,
            "placeholder": "https://blackpointpl.wpengine.com/wp-content/uploads/2021/05/bg_kalkulator1-40x35.png",
            "placeholder-height": 35,
            "placeholder-width": 40,
            "post-list": "https://blackpointpl.wpengine.com/wp-content/uploads/2021/05/bg_kalkulator1-431x323.png",
            "post-list-height": 323,
            "post-list-width": 431,
            "thumbnail": "https://blackpointpl.wpengine.com/wp-content/uploads/2021/05/bg_kalkulator1-150x150.png",
            "thumbnail-height": 150,
            "thumbnail-width": 150
          }
        },
        btn: {
          link: "",
          text: ""
        },
        counter: {
          description: "",
          value: "",
          valueCaption: ""
        },
        gallery: [{
          caption: "Umowę dla klienta",
          image: false
        }, {
          caption: "Zdjęcia dla mamy",
          image: false
        }, {
          caption: "Plakat dla znajomego",
          image: false
        }]
      }
    }
  },
  async asyncData ({ store, route, context }) { // this is for SSR purposes to prefetch data - and it's always executed before parent component methods
    if (context) context.output.cacheTags.add('category')
    await store.dispatch('category-next/loadCategories', {})
    await composeInitialPageState(store, route)
  },
  async beforeRouteEnter (to, from, next) {
    if (isServer) next() // SSR no need to invoke SW caching here
    else if (!from.name) { // SSR but client side invocation, we need to cache products and invoke requests from asyncData for offline support
      next(async vm => {
        vm.loading = true
        await composeInitialPageState(vm.$store, to, true)
        await vm.$store.dispatch('category-next/cacheProducts', { route: to }) // await here is because we must wait for the hydration
        vm.loading = false
      })
    } else { // Pure CSR, with no initial category state
      next(async vm => {
        vm.loading = true
        vm.$store.dispatch('category-next/cacheProducts', { route: to })
        vm.loading = false
      })
    }
  },
  methods: {
    openFilters () {
      this.mobileFilters = true
    },
    closeFilters () {
      this.mobileFilters = false
    },
    async changeFilter (filterVariant) {
      this.$store.dispatch('category-next/switchSearchFilters', [filterVariant])
    },
    columnChange (column) {
      this.defaultColumn = column
    },
    async onBottomScroll () {
      if (this.loadingProducts) return
      this.loadingProducts = true
      try {
        await this.$store.dispatch('category-next/loadMoreCategoryProducts')
      } catch (e) {
        Logger.error('Problem with fetching more products', 'category', e)()
      } finally {
        this.loadingProducts = false
      }
    },
    getCanonicalLink () {
      let category = this.getCurrentCategory
      let pathArray = category.path ? category.path.split('/') : []
      
      if(pathArray.lenght < 1){
        return []
      }      

      let canonicalLink = config.site.url + category.url_path      
      return {
        vmid: 'canonical',
        rel: 'canonical',
        href: canonicalLink
      }
    },
    getMetas () {
      const meta = []
      const { meta_description } = this.getCurrentCategory
      if (meta_description) {
        meta.push( {vmid: 'description', name: 'description', content: htmlDecode(meta_description)} )
      }
      // check if is set any filter
      if (Object.keys(this.getCurrentFilters).length > 0) {
        meta.push( { vmid: 'robots', name: 'robots', content: 'noindex' } );
      } else {
        meta.push( { vmid: 'robots', name: 'robots', content: 'index, follow' } );
      }
      return meta
    }
  },
  metaInfo () {
    const storeView = currentStoreView()
    const { meta_title, name } = this.getCurrentCategory
    return {
      link: [ 
        this.getCanonicalLink()
      ],
      title: htmlDecode(meta_title || name),
      meta: this.getMetas()       
    }
  }
}
</script>

<style lang="scss" scoped>
  .btn {
    &__filter {
      min-width: 100px;
    }
  }
  .divider {
    width: calc(100vw - 8px);
    bottom: 20px;
    left: -36px;
  }
  .category-filters {
    width: 242px;
  }

  .mobile-filters {
    display: none;
    overflow: auto;
  }

  .mobile-filters-button {
    display: none;
  }

  .mobile-sorting {
    display: none;
  }

  .category-title {
    line-height: 65px;
  }

  .justify-content-end {
    justify-content: flex-end;
  }

  .sorting {
    label {
      margin-right: 10px;
    }
  }

  .pl15 {
    padding-left: 15px;
  }

  .products-list-container {
    border-radius: 30px;

    h1 {
      margin: 0;
    }
  }

  @media (max-width: 64em) {
    .products-list {
      max-width: 530px;
    }
  }

  @media (max-width: 770px) {
    .category-title {
      margin: 0;
      font-size: 25px;
      line-height: 40px;
    }

    .products-list {
      width: 100%;
      max-width: none;
    }

    .mobile-filters {
      display: block;
    }

    .mobile-filters-button {
      display: block;
      height: 45px;
    }

    .sorting {
      display: none;
    }

    .mobile-sorting {
      display: block;
    }

    .category-filters {
      display: none;
    }

    .product-listing {
      justify-content: center;;
    }

    .mobile-filters {
      position: fixed;
      background-color: #F2F2F2;
      z-index: 5;
      padding: 20px 10px;
      left: 0;
      width: 100vw;
      height: calc(100vh - 110px);
      top: 110px;
      box-sizing: border-box;
    }

    .mobile-filters-body {
      padding-top: 50px;
    }
  }

  .close-container {
    left: 0;
  }

  .close {
    margin-left: auto;
  }
</style>
<style lang="scss">
.product-image {
  max-height: unset !important;
}
</style>
